import { useState, type JSX } from "react";
import CustomButton from "../Button";
import AppTooltip from '../Tooltip';
import RiskPlusReportConfirmModal from "../RiskPlusReportConfirmModal";
import { AuthStore } from "../../stores";
import { UseCaseChat } from "../../entities/useCaseChat/UseCaseChat";
import { getUseCaseService } from "../../services/UseCaseService";

export interface GenerateReportButtonProps {
  useCaseChat: UseCaseChat;
  onGenerateReport?: () => void;
  label?: string;
  reportType?: "risk" | "impact";
  disabled?: boolean;
  variant?: "solid" | "outline" | "icon";
}

export const GenerateReportButton = ({ useCaseChat, onGenerateReport, label, reportType = "risk", disabled: disabledProp = false, variant = "solid" }: GenerateReportButtonProps): JSX.Element => {

  const resolvedLabel = typeof label === "string" && label.trim().length > 0 ? label : "Generate Report";
  const isRefresh = /^refresh/i.test(resolvedLabel);

  const riskReportCredits = AuthStore.useAuthStore((state) => state.license.riskPlusReportCredits);
  const riskRefreshCredits = AuthStore.useAuthStore((state) => state.license.riskPlusReportRefreshCredits);
  const impactReportCredits = AuthStore.useAuthStore((state) => state.license.impactPlusReportCredits);
  const impactRefreshCredits = AuthStore.useAuthStore((state) => state.license.impactPlusReportRefreshCredits);
  const reportCredits = reportType === "impact" ? impactReportCredits : riskReportCredits;
  const refreshCredits = reportType === "impact" ? impactRefreshCredits : riskRefreshCredits;
  const reportGenerationCredits = isRefresh ? refreshCredits : reportCredits;

  const sufficientCredits = reportGenerationCredits != null && reportGenerationCredits > 0;

  const contentIsSufficient = useCaseChat.isSufficientForReportGeneration;

  const canGenerate = contentIsSufficient;
  const toolTipMsg = disabledProp
    ? "Report generation in progress"
    : (!contentIsSufficient ? "Content is not sufficient: Use Chat to augment" : (sufficientCredits ? "Generate for 1 credit" : "Insufficient credits"));
  const tooltipContent = variant === "icon" ? resolvedLabel : toolTipMsg;

  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false);
  const [resolvedUseCaseChat, setResolvedUseCaseChat] = useState<UseCaseChat>(useCaseChat);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const handleOpenModal = async () => {
    if (!canGenerate) return;

    const hasFullData = useCaseChat.useCase?.useCaseSummary?.useCaseTitle;
    if (hasFullData) {
      setResolvedUseCaseChat(useCaseChat);
      setConfirmModalIsOpen(true);
      return;
    }

    setIsFetchingDetails(true);
    try {
      const full = await getUseCaseService().getUseCaseById(useCaseChat.id);
      setResolvedUseCaseChat(full);
      setConfirmModalIsOpen(true);
    } catch (err) {
      console.error("Failed to load use case details:", err);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleConfirmReportGeneration = async () => {
    setConfirmModalIsOpen(false);
    if (onGenerateReport) {
      onGenerateReport();
    }
  }


  return (
    <>
      <AppTooltip
        content={tooltipContent}
        placement="top"
        showArrow
      >
        <span className="inline-block">
          {variant === "icon" ? (
            <CustomButton
              data-testid={canGenerate ? "generate-report-button" : "disabled-generate-report-button"}
              type="button"
              aria-label={resolvedLabel}
              color={undefined}
              className={`!h-8 !w-8 !min-h-8 !min-w-0 !p-0 rounded-[6px] bg-white border border-royal-blue text-royal-blue hover:bg-royal-blue/5 disabled:bg-white disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed inline-flex items-center justify-center ${canGenerate ? "" : "cursor-not-allowed"}`}
              disabled={!canGenerate || isFetchingDetails || disabledProp}
              onPress={handleOpenModal}
            >
              <span className="aq-icon text-[16px]" aria-hidden>
                {isFetchingDetails ? "hourglass_empty" : "refresh"}
              </span>
            </CustomButton>
          ) : (
            <CustomButton
              data-testid={canGenerate ? "generate-report-button" : "disabled-generate-report-button"}
              type="button"
              aria-label={resolvedLabel}
              color={variant === "outline" ? undefined : "primary"}
              className={`!h-auto !min-h-7 !py-1 !text-xxs !leading-tight rounded-[6px] px-4 font-semibold whitespace-nowrap inline-flex items-center gap-1.5 ${variant === "outline"
                ? `bg-white border border-royal-blue text-royal-blue hover:bg-royal-blue/5 disabled:bg-white disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed ${canGenerate ? "" : "cursor-not-allowed"}`
                : (canGenerate ? "text-white" : "cursor-not-allowed")
                }`}
              disabled={!canGenerate || isFetchingDetails || disabledProp}
              onPress={handleOpenModal}
            >
              {isRefresh && !isFetchingDetails && (
                <span className="aq-icon text-[14px]" aria-hidden>
                  refresh
                </span>
              )}
              {isFetchingDetails ? "Loading…" : resolvedLabel}
            </CustomButton>
          )}
        </span>
      </AppTooltip>

      <RiskPlusReportConfirmModal
        useCaseChat={resolvedUseCaseChat}
        isOpen={confirmModalIsOpen}
        mode="generate"
        reportType={reportType}
        isRefresh={isRefresh}
        onClose={() => setConfirmModalIsOpen(false)}
        onConfirmGeneration={handleConfirmReportGeneration}
      />
    </>
  );
};


